"use client";

import { useState } from "react";
import { useAccount } from "wagmi";
import { useTRPC } from "@/trpc/react";
import { useQuery } from "@tanstack/react-query";
import { GameWalletConnect } from "../wallet-connect";

export default function ForgePage() {
  const { address, isConnected } = useAccount();
  const trpc = useTRPC();
  const [item1Id, setItem1Id] = useState<string | null>(null);
  const [item2Id, setItem2Id] = useState<string | null>(null);

  const { data: loot } = useQuery(
    trpc.loot.getByOwner.queryOptions(
      { walletAddress: address ?? "0x0000000000000000000000000000000000000000" },
      { enabled: isConnected && !!address }
    )
  );

  if (!isConnected) {
    return (
      <div className="py-20 text-center">
        <h1 className="text-3xl font-black text-amber-400 uppercase tracking-wider mb-6">The Forge</h1>
        <p className="text-white/40 mb-8">Connect your wallet to forge your gear.</p>
        <GameWalletConnect />
      </div>
    );
  }

  const selected1 = loot?.find((i) => i.id === item1Id);
  const selected2 = loot?.find((i) => i.id === item2Id);
  const availableForSlot2 = loot?.filter((i) => i.id !== item1Id && (!selected1 || i.itemType === selected1.itemType));

  return (
    <div className="mx-auto max-w-7xl px-4 py-12">
      <h1 className="text-3xl font-black text-amber-400 uppercase tracking-wider mb-2">The Forge</h1>
      <p className="text-sm text-white/25 mb-8">
        Combine two items of the same type into a stronger one. Costs 0.2 AVAX. Burns both inputs.
      </p>

      {/* Forge Slots */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center mb-12">
        {/* Slot 1 */}
        <div className="bg-white/[0.03] border-2 border-dashed border-white/10 rounded-xl p-6 min-h-[200px] flex flex-col items-center justify-center">
          {selected1 ? (
            <>
              <span className="text-3xl mb-2">⚔️</span>
              <h3 className="font-black text-amber-400 uppercase text-sm">{selected1.template?.name}</h3>
              <p className="text-xs text-white/40">{selected1.rarity} | +{selected1.level}</p>
              <button onClick={() => setItem1Id(null)} className="mt-2 text-xs text-red-400 hover:underline">Remove</button>
            </>
          ) : (
            <p className="text-white/25 text-sm font-bold">Select Item 1</p>
          )}
        </div>

        {/* Center */}
        <div className="text-center">
          <div className="text-4xl text-amber-400 mb-4">🔨</div>
          <button
            disabled={!selected1 || !selected2}
            className="px-8 py-3 rounded-lg bg-green-500 text-black font-black uppercase disabled:opacity-40 disabled:cursor-not-allowed hover:bg-green-600 transition-colors"
          >
            Forge (0.2 AVAX)
          </button>
        </div>

        {/* Slot 2 */}
        <div className="bg-white/[0.03] border-2 border-dashed border-white/10 rounded-xl p-6 min-h-[200px] flex flex-col items-center justify-center">
          {selected2 ? (
            <>
              <span className="text-3xl mb-2">⚔️</span>
              <h3 className="font-black text-amber-400 uppercase text-sm">{selected2.template?.name}</h3>
              <p className="text-xs text-white/40">{selected2.rarity} | +{selected2.level}</p>
              <button onClick={() => setItem2Id(null)} className="mt-2 text-xs text-red-400 hover:underline">Remove</button>
            </>
          ) : (
            <p className="text-white/25 text-sm font-bold">Select Item 2</p>
          )}
        </div>
      </div>

      {/* Item Selection Grid */}
      <h2 className="text-xl font-black text-white/50 uppercase tracking-wider mb-4">Your Gear</h2>
      {!loot || loot.length === 0 ? (
        <p className="text-white/25 text-sm">No gear to forge. Play some rounds first!</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {(item1Id ? availableForSlot2 : loot)?.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                if (!item1Id) setItem1Id(item.id);
                else if (!item2Id) setItem2Id(item.id);
              }}
              disabled={!!item1Id && !!item2Id}
              className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3 text-center hover:border-green-500/30 transition-colors disabled:opacity-40"
            >
              <span className="text-xl">⚔️</span>
              <p className="text-xs font-black text-amber-400 uppercase mt-1 truncate">{item.template?.name}</p>
              <p className="text-xs text-white/25">{item.rarity}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
